import { useEffect, useRef, useState } from 'react';
import { attendanceQrEmailSchema, type AttendanceQrView } from 'shared-models/browser';
import { attendanceService as api } from '../../../../services/attendance/attendance.service';
import { downloadFile } from '../../../../utils/download-file.util';
import { printHtmlDocument } from '../../../../utils/print-html.util';

export type AttendanceQrControllerProps = { opened: boolean; tenantId: string; employeeSession: string };
export function useAttendanceQrController({ opened, tenantId, employeeSession }: AttendanceQrControllerProps) {
  const [qr, setQr] = useState<AttendanceQrView | null>(null);
  const [busy, setBusy] = useState('');
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const [recipient, setRecipient] = useState('');
  const [retry, setRetry] = useState(0);
  const lock = useRef(false);
  const revision = useRef(0);
  useEffect(() => {
    if (!opened) return;
    const started = ++revision.current;
    setQr(null); setBusy('load'); setError(''); setNotice(''); setRecipient('');
    api.getQr(tenantId, employeeSession).then((value) => { if (started === revision.current) setQr(value); })
      .catch(() => { if (started === revision.current) setError('טעינת קוד הנוכחות נכשלה'); })
      .finally(() => { if (started === revision.current) setBusy(''); });
    return () => { revision.current++; };
  }, [opened, tenantId, employeeSession, retry]);

  const run = async (operation: string, action: (current: () => boolean) => Promise<void>) => {
    if (!opened || !qr || busy || lock.current) return;
    lock.current = true; setBusy(operation); setError(''); setNotice('');
    const started = revision.current;
    try { await action(() => started === revision.current); }
    catch { if (started === revision.current) setError('הפעולה נכשלה. נסו שוב.'); }
    finally { lock.current = false; if (started === revision.current) setBusy(''); }
  };
  const pdf = () => run('pdf', async (current) => {
    const blob = await api.getQrPdf(tenantId, employeeSession);
    if (!current()) return;
    const url = URL.createObjectURL(blob);
    downloadFile(url, 'attendance-qr.pdf'); window.setTimeout(() => URL.revokeObjectURL(url), 1000);
  });
  const email = () => {
    const parsed = attendanceQrEmailSchema.safeParse({ email: recipient });
    if (!parsed.success) { setError('יש להזין כתובת מייל תקינה'); return; }
    return run('email', async (current) => { await api.emailQr(tenantId, employeeSession, parsed.data.email); if (current()) setNotice('המייל נשלח'); });
  };
  const print = () => run('print', async () => {
    const doc = document.implementation.createHTMLDocument('QR לנוכחות');
    doc.documentElement.lang = 'he'; doc.documentElement.dir = 'rtl';
    const heading = doc.createElement('h1'); heading.textContent = 'כניסה ויציאה ממשמרת';
    const image = doc.createElement('img'); image.src = qr!.qrDataUrl; image.alt = 'QR לנוכחות'; image.width = 450; image.height = 450;
    const style = doc.createElement('style'); style.textContent = 'body{text-align:center;font-family:sans-serif;padding:32px}img{max-width:100%;height:auto}';
    doc.head.appendChild(style); doc.body.append(heading, image);
    printHtmlDocument(`<!doctype html>${doc.documentElement.outerHTML}`);
  });
  return { qr, busy, error, notice, recipient, setRecipient, pdf, email, print,
    png: () => run('png', async () => downloadFile(qr!.qrDataUrl, 'attendance-qr.png')),
    retry: () => setRetry((previous) => previous + 1),
  };
}